import { Injectable } from '@angular/core';
import { PaginatedState, PaginatedStateModel } from '@shared/ui';

import { PipelineData } from './pipeline.state';

/**
 * Context for the manager instance — which method type owns this assigned list,
 * and whether that method type is already persisted (a staged, brand-new method
 * type has no `ProtocolPipeline` rows to fetch, so its assigned list is purely
 * the session's staged assignments).
 */
export interface MethodPipelineContext {
  methodTypeId: string | undefined;
  persisted: boolean;
}

/**
 * Everything one method type's assigned pipelines amount to in a session:
 * - `persistedAssigned` — the full set assigned in the DB, fetched once on init.
 * - `persistedLoaded` — whether that fetch has happened for this method type.
 * - `stagedAssigned` — pipelines assigned this session but not yet persisted
 *   (keyed by pipeline id): brand-new drafts and catalog picks not in the
 *   persisted set.
 * - `stagedUnassignedIds` — persisted-assigned pipelines un-assigned this session,
 *   filtered out of the combined set until a save reconciles them.
 */
export interface MethodTypePipelineStaging {
  persistedAssigned: PipelineData[];
  persistedLoaded: boolean;
  stagedAssigned: Record<string, PipelineData>;
  stagedUnassignedIds: string[];
}

/**
 * The staging is a single map from method-type id to that method type's whole
 * slice, so this shared instance can remember EVERY method type's session
 * staging simultaneously (the `AssignedResourcesState` `containers` pattern: one
 * map from owner → everything about it). One editor-manager session opens the
 * dialog for many method types in turn; switching A→B→A must not lose A's staged
 * assignments. `methodTypeId` is just the CURRENT key — the method type whose
 * slice the selectors/updaters read and write, and whose page the inherited
 * (single) `items` window renders (only one dialog is open at a time).
 */
export interface AssignedPipelinesStateModel extends PaginatedStateModel<PipelineData> {
  /** The current method type (which keyed slice the selectors/updaters resolve to). */
  methodTypeId: string | undefined;
  methodTypePersisted: boolean;
  byMethodType: Record<string, MethodTypePipelineStaging>;
}

/**
 * The slice returned for a method type with no entry yet. Shared (frozen) so the
 * field selectors project STABLE references for a not-yet-loaded method type and
 * never emit spuriously on unrelated state changes.
 */
const EMPTY_SLICE: MethodTypePipelineStaging = Object.freeze({
  persistedAssigned: Object.freeze([]) as unknown as PipelineData[],
  persistedLoaded: false,
  stagedAssigned: Object.freeze({}) as unknown as Record<string, PipelineData>,
  stagedUnassignedIds: Object.freeze([]) as unknown as string[],
});

const ADDITIONAL_INITIAL_STATE: Omit<AssignedPipelinesStateModel, keyof PaginatedStateModel<PipelineData>> = {
  methodTypeId: undefined,
  methodTypePersisted: false,
  byMethodType: {},
};

/**
 * Paginated state for the right list of the pipeline manager — the pipelines
 * assigned to a method type. Page size is fixed at 10 and there is no search.
 * The "ground truth" of what is assigned is the combined set computed here for
 * the current method type (persisted − staged-unassigned ∪ staged-assigned); the
 * items adapter only ever holds the current page of that combined set.
 */
@Injectable()
export class AssignedPipelinesState extends PaginatedState<PipelineData, AssignedPipelinesStateModel> {
  constructor() {
    super({ useMemory: false }, { ...ADDITIONAL_INITIAL_STATE });
  }

  // ============================================================================
  // Selectors (resolve the slice of the current `methodTypeId`)
  // ============================================================================

  public readonly methodTypeId$ = this.select((s) => s.methodTypeId);
  public readonly methodTypePersisted$ = this.select((s) => s.methodTypePersisted);
  /*
   * The RAW method-type → staging map, pointer-independent. Dirty-tracking
   * consumers (`MethodTypeEditorEffects.currentSignature$`) pick their own
   * slice by the editor's id — the pointer-based
   * `stagedAssigned$`/`stagedUnassignedIds$` follow the mutable `setContext`
   * key, which the embedded manager may move AFTER the editor seeds.
   */
  public readonly byMethodType$ = this.select((s) => s.byMethodType);
  public readonly persistedAssigned$ = this.select((s) => this.sliceFor(s, s.methodTypeId).persistedAssigned);
  public readonly persistedLoaded$ = this.select((s) => this.sliceFor(s, s.methodTypeId).persistedLoaded);
  public readonly stagedAssigned$ = this.select((s) => this.sliceFor(s, s.methodTypeId).stagedAssigned);
  public readonly stagedUnassignedIds$ = this.select((s) => this.sliceFor(s, s.methodTypeId).stagedUnassignedIds);

  /** The full assigned set (persisted − unassigned ∪ staged) for the current method type, unsorted. */
  public readonly combinedAssigned$ = this.select((s) => this.combine(this.sliceFor(s, s.methodTypeId)));

  /** Set of currently-assigned ids — the source the left list derives its flag from. */
  public readonly assignedIds$ = this.select(
    (s) => new Set(this.combine(this.sliceFor(s, s.methodTypeId)).map((p) => p.id))
  );

  // ============================================================================
  // Updaters (mutate the slice of the current `methodTypeId`; no-op when unset)
  // ============================================================================

  public readonly setContext = this.updater((state, ctx: MethodPipelineContext) => ({
    ...state,
    methodTypeId: ctx.methodTypeId,
    methodTypePersisted: ctx.persisted,
  }));

  public readonly setPersistedAssigned = this.updater((state, persistedAssigned: PipelineData[]) =>
    this.patchCurrent(state, (slice) => ({ ...slice, persistedAssigned, persistedLoaded: true }))
  );

  /**
   * Stage a pipeline as assigned. A persisted pipeline only needs un-marking as
   * removed; a not-currently-persisted one (catalog pick or brand-new draft) is
   * recorded with its data so the combined set can render it.
   */
  public readonly assign = this.updater((state, pipeline: PipelineData) =>
    this.patchCurrent(state, (slice) => {
      const stagedUnassignedIds = slice.stagedUnassignedIds.filter((id) => id !== pipeline.id);
      const persistedHas = slice.persistedAssigned.some((p) => p.id === pipeline.id);
      const stagedAssigned = persistedHas
        ? slice.stagedAssigned
        : { ...slice.stagedAssigned, [pipeline.id]: pipeline };
      return { ...slice, stagedAssigned, stagedUnassignedIds };
    })
  );

  /** Un-stage a pipeline. Persisted ones are recorded as removed; staged ones just drop. */
  public readonly unassign = this.updater((state, pipelineId: string) =>
    this.patchCurrent(state, (slice) => {
      const stagedAssigned = { ...slice.stagedAssigned };
      delete stagedAssigned[pipelineId];
      const persistedHas = slice.persistedAssigned.some((p) => p.id === pipelineId);
      const stagedUnassignedIds =
        persistedHas && !slice.stagedUnassignedIds.includes(pipelineId)
          ? [...slice.stagedUnassignedIds, pipelineId]
          : slice.stagedUnassignedIds;
      return { ...slice, stagedAssigned, stagedUnassignedIds };
    })
  );

  /**
   * Patch an assigned pipeline's data (name/env) after it was edited in the
   * dialog, so the right list reflects the edit without changing assignment.
   */
  public readonly updateAssignedPipeline = this.updater((state, pipeline: PipelineData) =>
    this.patchCurrent(state, (slice) => {
      const persistedAssigned = slice.persistedAssigned.map((p) =>
        p.id === pipeline.id ? { ...p, ...pipeline } : p
      );
      const stagedAssigned = slice.stagedAssigned[pipeline.id]
        ? { ...slice.stagedAssigned, [pipeline.id]: { ...slice.stagedAssigned[pipeline.id], ...pipeline } }
        : slice.stagedAssigned;
      return { ...slice, persistedAssigned, stagedAssigned };
    })
  );

  /**
   * After persistence, swap client temp pipeline ids for the REAL ids the saves
   * returned (`AssignedResourcesState.remapIds` parity), within the CURRENT
   * method type's staged set: entries are re-keyed and their `id` patched. Ids
   * absent from the map stay put; `stagedUnassignedIds` never needs remapping
   * (only persisted-assigned pipelines — real DB ids — ever land there).
   */
  public readonly remapIds = this.updater((state, idMap: Record<string, string>) =>
    this.patchCurrent(state, (slice) => {
      const stagedAssigned: Record<string, PipelineData> = {};
      for (const oldId of Object.keys(slice.stagedAssigned)) {
        const newId = idMap[oldId] ?? oldId;
        stagedAssigned[newId] = { ...slice.stagedAssigned[oldId], id: newId };
      }
      return { ...slice, stagedAssigned };
    })
  );

  /**
   * Re-key a whole method type's slice from a client temp id to the real id the
   * method-type save returned. A brand-new method type is edited under a
   * `tmp-<uuid>` id; once persisted, its staged pipelines must follow to the real
   * id so the assigned-pipelines join save reads them under the right key.
   * Mirrors `AssignedResourcesState.remapIds` (container-key remap) and
   * `LabelConfigState.remapMethodTypeId`. Also moves the current pointer when it
   * was the remapped key.
   */
  public readonly remapMethodTypeId = this.updater((state, payload: { from: string; to: string }) => {
    const { from, to } = payload;
    if (from === to || !(from in state.byMethodType)) return state;

    const byMethodType = { ...state.byMethodType };
    byMethodType[to] = byMethodType[from];
    delete byMethodType[from];

    return {
      ...state,
      methodTypeId: state.methodTypeId === from ? to : state.methodTypeId,
      byMethodType,
    };
  });

  /** Clear the CURRENT method type's session staging (after a save folds it into persisted). */
  public readonly clearStaging = this.updater((state) =>
    this.patchCurrent(state, (slice) => ({ ...slice, stagedAssigned: {}, stagedUnassignedIds: [] }))
  );

  // ============================================================================
  // Helpers
  // ============================================================================

  /** The current method type's slice, or the shared empty slice when there is none. */
  private sliceFor(state: AssignedPipelinesStateModel, id: string | undefined): MethodTypePipelineStaging {
    return (id && state.byMethodType[id]) || EMPTY_SLICE;
  }

  /**
   * Apply `change` to the current method type's slice (seeded from the empty
   * slice if it has none yet). A no-op when there is no current method type, so
   * stray writes before a context is set cannot create an orphan entry.
   */
  private patchCurrent(
    state: AssignedPipelinesStateModel,
    change: (slice: MethodTypePipelineStaging) => MethodTypePipelineStaging
  ): AssignedPipelinesStateModel {
    const id = state.methodTypeId;
    if (!id) return state;
    return { ...state, byMethodType: { ...state.byMethodType, [id]: change(this.sliceFor(state, id)) } };
  }

  private combine(slice: MethodTypePipelineStaging): PipelineData[] {
    const unassigned = new Set(slice.stagedUnassignedIds);
    const byId = new Map<string, PipelineData>();
    for (const p of slice.persistedAssigned) {
      if (!unassigned.has(p.id)) byId.set(p.id, p);
    }
    for (const pipelineId of Object.keys(slice.stagedAssigned)) {
      byId.set(pipelineId, slice.stagedAssigned[pipelineId]);
    }
    return Array.from(byId.values());
  }
}
